import directory from '@/api/adminDirectory';
import therapyService from '@/api/therapyDetails';

function toError(e) {
    return {
        status: e?.response?.status || 0,
        message: e?.response?.data?.message || ''
    };
}

export const state = {
    directory: directory('admin'),
    therapyService: therapyService('doctor'),
    assignments: [],
    assignmentsLoading: false,
    assignmentsError: null,
    therapies: [],
    therapiesLoading: false,
    therapiesError: null,
    compliance: {},
    complianceLoading: false,
    events: [],
    eventsLoading: false,
    eventsError: null
};

export const getters = {
    assignments: function (state) {
        return state.assignments;
    },
    assignmentsLoading: function (state) {
        return !!state.assignmentsLoading;
    },
    assignmentsError: function (state) {
        return state.assignmentsError;
    },
    therapies: function (state) {
        return state.therapies;
    },
    therapiesLoading: function (state) {
        return !!state.therapiesLoading;
    },
    therapiesError: function (state) {
        return state.therapiesError;
    },
    compliance: function (state) {
        return state.compliance;
    },
    complianceLoading: function (state) {
        return !!state.complianceLoading;
    },
    events: function (state) {
        return state.events;
    },
    eventsLoading: function (state) {
        return !!state.eventsLoading;
    },
    eventsError: function (state) {
        return state.eventsError;
    }
};

export const mutations = {
    'SET_ASSIGNMENTS': function (state, assignments = []) {
        state.assignments = assignments;
    },
    'SET_ASSIGNMENTS_LOADING': function (state, boolean = true) {
        state.assignmentsLoading = boolean;
    },
    'SET_ASSIGNMENTS_ERROR': function (state, error = null) {
        state.assignmentsError = error;
    },
    'SET_THERAPIES': function (state, therapies = []) {
        state.therapies = therapies;
    },
    'SET_THERAPIES_LOADING': function (state, boolean = true) {
        state.therapiesLoading = boolean;
    },
    'SET_THERAPIES_ERROR': function (state, error = null) {
        state.therapiesError = error;
    },
    'SET_COMPLIANCE': function (state, compliance = {}) {
        state.compliance = compliance;
    },
    'SET_COMPLIANCE_LOADING': function (state, boolean = true) {
        state.complianceLoading = boolean;
    },
    'SET_EVENTS': function (state, events = []) {
        state.events = events;
    },
    'SET_EVENTS_LOADING': function (state, boolean = true) {
        state.eventsLoading = boolean;
    },
    'SET_EVENTS_ERROR': function (state, error = null) {
        state.eventsError = error;
    }
};

export const actions = {
    assignments: function ({commit, state}, params) {
        commit('SET_ASSIGNMENTS_LOADING', true);
        commit('SET_ASSIGNMENTS_ERROR', null);

        return state.directory.fetchAssignments(params).then(response => {
            commit('SET_ASSIGNMENTS', response?.data || []);
            commit('SET_ASSIGNMENTS_LOADING', false);

            return response?.data || [];
        }).catch(e => {
            commit('SET_ASSIGNMENTS_ERROR', toError(e));
            commit('SET_ASSIGNMENTS_LOADING', false);

            return [];
        });
    },

    therapies: function ({commit, state}, pseudonymId) {
        commit('SET_THERAPIES_LOADING', true);
        commit('SET_THERAPIES_ERROR', null);

        return state.therapyService.fetchForPatient(pseudonymId).then(response => {
            const therapies = response?.data || [];

            commit('SET_THERAPIES', therapies);
            commit('SET_THERAPIES_LOADING', false);

            return therapies;
        }).catch(e => {
            commit('SET_THERAPIES_ERROR', toError(e));
            commit('SET_THERAPIES_LOADING', false);

            return [];
        });
    },

    compliance: function ({commit, state}, therapies) {
        const list = (therapies || []).filter(therapy => {
            return ['active', 'paused', 'closed'].includes(therapy.status);
        });

        if (!list.length) {
            commit('SET_COMPLIANCE', {});

            return Promise.resolve({});
        }

        commit('SET_COMPLIANCE_LOADING', true);

        return Promise.all(list.map(therapy => {
            return state.therapyService.fetchCompliance(therapy.id).then(response => {
                return [therapy.id, response];
            }).catch(() => {
                return [therapy.id, null];
            });
        })).then(entries => {
            const map = {};

            entries.forEach(([id, value]) => {
                if (value) {
                    map[id] = value;
                }
            });

            commit('SET_COMPLIANCE', map);
            commit('SET_COMPLIANCE_LOADING', false);

            return map;
        });
    },

    events: function ({commit, state}, entityId) {
        commit('SET_EVENTS_LOADING', true);
        commit('SET_EVENTS_ERROR', null);

        return state.directory.fetchAuditEvents({entity_id: entityId, per_page: 50}).then(response => {
            commit('SET_EVENTS', response?.data || []);
            commit('SET_EVENTS_LOADING', false);

            return response?.data || [];
        }).catch(e => {
            commit('SET_EVENTS_ERROR', toError(e));
            commit('SET_EVENTS_LOADING', false);

            return [];
        });
    },

    clear: function ({commit}) {
        commit('SET_THERAPIES', []);
        commit('SET_THERAPIES_ERROR', null);
        commit('SET_COMPLIANCE', {});
        commit('SET_EVENTS', []);
        commit('SET_EVENTS_ERROR', null);
    }
};
